import { ThunkDispatch } from 'redux-thunk';
import { AnyAction } from 'redux';
import * as actionTypes from '../action-types';
import {
  fetchWatchers,
  fetchWatcher,
  markAllWatcherDocumentsAsRead,
  createQueriesOnWatcherRequest,
  deleteQueryOnWatcherRequest,
  deleteWatcherRequest,
  createWatcherRequest,
} from '../../watchers.api';
import {
  WatcherWithoutDocuments,
  WatcherSearchQueryEnum,
  WatcherSearchQuery,
  WatcherWithoutDocumentsWithCounts,
} from '../../watchers.types';
import {
  sendWatcherDeletedEvent,
  sendWatcherCreatedEvent,
} from '../../WatchersUtils';
import {
  removeQueryFromWatcherExists,
  addQueriesToWatcherExists,
  unsetWatcherExists,
  setWatcherExists,
} from './watcherExists';

const setWatcher = (watcher: WatcherWithoutDocuments) =>
  <const>{
    type: actionTypes.SET_WATCHER,
    payload: { watcher },
  };

const setWatcherWithCounts = (watcher: WatcherWithoutDocumentsWithCounts) =>
  <const>{
    type: actionTypes.SET_WATCHER_WITH_COUNTS,
    payload: { watcher },
  };

const setWatchers = (watchers: Array<WatcherWithoutDocumentsWithCounts>) =>
  <const>{
    type: actionTypes.SET_WATCHERS,
    payload: { watchers },
  };

const deleteWatcher = (watcherKey: string) =>
  <const>{
    type: actionTypes.DELETE_WATCHER,
    payload: { watcherKey },
  };

const deleteWatcherQuery = (watcherKey: string, type: WatcherSearchQueryEnum) =>
  <const>{
    type: actionTypes.DELETE_WATCHER_QUERY,
    payload: { watcherKey, type },
  };

const markDocumentAsRead = (
  watcherKey: string,
  documentType: WatcherSearchQueryEnum,
) =>
  <const>{
    type: actionTypes.SET_DOCUMENT_AS_READ,
    payload: { watcherKey, documentType },
  };

const markAllDocumentsAsRead = (watcherKey: string) =>
  <const>{
    type: actionTypes.SET_ALL_DOCUMENTS_AS_READ,
    payload: { watcherKey },
  };

export const addNewWatcherToList = (watcher: WatcherWithoutDocuments) =>
  <const>{
    type: actionTypes.CREATE_WATCHER,
    payload: { watcher },
  };

export const getWatcher = (watcherKey: string) => async (
  dispatch: ThunkDispatch<{}, {}, AnyAction>,
) => {
  const watcher = await fetchWatcher(watcherKey);
  dispatch(setWatcherWithCounts(watcher));
  return watcher;
};

export const getWatchersList = () => async (
  dispatch: ThunkDispatch<{}, {}, AnyAction>,
) => {
  const watchers = await fetchWatchers();
  dispatch(setWatchers(watchers));
};

export const removeWatcherFromList = (watcherKey?: string) => (
  dispatch: ThunkDispatch<{}, {}, AnyAction>,
  getState: Function,
) => {
  const { watchers } = getState();
  if (watcherKey && watchers.keys.includes(watcherKey)) {
    dispatch(deleteWatcher(watcherKey));
  }
};

export const setDocumentAsRead = (
  watcherKey: string,
  documentType: WatcherSearchQueryEnum,
) => (dispatch: ThunkDispatch<{}, {}, AnyAction>) => {
  dispatch(markDocumentAsRead(watcherKey, documentType));
};

export const setAllDocumentsAsRead = (watcherKey: string) => async (
  dispatch: ThunkDispatch<{}, {}, AnyAction>,
) => {
  await markAllWatcherDocumentsAsRead(watcherKey);
  dispatch(markAllDocumentsAsRead(watcherKey));
};

export const createWatcherWithTracking = (
  searchQueries: Array<WatcherSearchQuery>,
  source: string,
) => async (dispatch: ThunkDispatch<{}, {}, AnyAction>) => {
  try {
    const watcher = await createWatcherRequest(searchQueries);
    if (!watcher.alreadyExists) {
      sendWatcherCreatedEvent({
        key: watcher.key,
        source,
        reportOriginalQueryKey: true,
        searchQueries,
      });
      dispatch(
        setWatcherExists(
          watcher.key,
          searchQueries.map((searchQuery) => searchQuery.type),
        ),
      );

      dispatch(addNewWatcherToList(watcher));
    }
    return watcher;
  } catch (error) {
    const conflict = error.response && error.response.status === 409;
    if (!conflict) {
      throw error;
    }
  }
};

export const deleteWatcherWithTracking = (
  watcherKey: string,
  source: string,
) => async (dispatch: ThunkDispatch<{}, {}, AnyAction>) => {
  try {
    await deleteWatcherRequest(watcherKey);
    sendWatcherDeletedEvent({
      key: watcherKey,
      source,
      type: 'all',
    });
    await dispatch(removeWatcherFromList(watcherKey));
    await dispatch(unsetWatcherExists());
  } catch (error) {
    throw error;
  }
};

export const createWatcherQueriesWithTracking = (
  watcherKey: string,
  searchQueries: Array<WatcherSearchQuery>,
  source: string,
) => async (dispatch: ThunkDispatch<{}, {}, AnyAction>) => {
  const watcher = await createQueriesOnWatcherRequest(
    watcherKey,
    searchQueries,
  );
  await dispatch(setWatcher(watcher));
  sendWatcherCreatedEvent({
    key: watcherKey,
    source,
    reportOriginalQueryKey: true,
    searchQueries,
  });
  await dispatch(
    addQueriesToWatcherExists(
      watcherKey,
      searchQueries.map((searchQuery) => searchQuery.type),
    ),
  );
};

export const deleteWatcherQueryWithTracking = (
  watcherKey: string,
  type: WatcherSearchQueryEnum,
  source: string,
) => async (dispatch: ThunkDispatch<{}, {}, AnyAction>) => {
  await deleteQueryOnWatcherRequest(watcherKey, type);
  await dispatch(deleteWatcherQuery(watcherKey, type));
  sendWatcherDeletedEvent({
    key: watcherKey,
    source,
    type,
  });
  await dispatch(removeQueryFromWatcherExists(watcherKey, type));
};

export type WatchersActions = ReturnType<
  | typeof setWatcher
  | typeof setWatcherWithCounts
  | typeof setWatchers
  | typeof addNewWatcherToList
  | typeof deleteWatcher
  | typeof markDocumentAsRead
  | typeof markAllDocumentsAsRead
  | typeof deleteWatcherQuery
>;
